import {
  Box, Container, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Skeleton, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, Alert, CircularProgress
} from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchAllPayouts, retryPayout } from "../../../redux/admin-slices/wallet.slice"
import TablePagination from "../../../components/common/PaginationSkell"
import { formatDate } from "../../../utils/helper"

const fmt = (n, cur = "inr") => {
  const isUsd = cur?.toLowerCase() === "usd";
  const symbol = isUsd ? "$" : "₹";
  const locale = isUsd ? "en-US" : "en-IN";
  return `${symbol}${(n / 100).toLocaleString(locale, { minimumFractionDigits: 2 })}`;
}

// ─── Status chip ─────────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const cfg = {
    succeeded:  { label: "Succeeded",  color: "success" },
    failed:     { label: "Failed",     color: "error"   },
    processing: { label: "Processing", color: "warning" },
  }[status] || { label: status, color: "default" }
  return (
    <Chip size="small" label={cfg.label} color={cfg.color}
      sx={{ fontWeight: 600, minWidth: 90, textTransform: "capitalize" }} />
  )
}

// ─── Retry confirmation dialog ────────────────────────────────────────────────
function RetryDialog({ open, payout, onClose, onSuccess }) {
  const dispatch   = useDispatch()
  const [loading, setLoading] = useState(false)

  const handleRetry = async () => {
    setLoading(true)
    try {
      await dispatch(retryPayout({ payoutId: payout._id })).unwrap()
      onSuccess?.()
      onClose()
    } catch { /* toast */ }
    finally { setLoading(false) }
  }

  return (
    <Dialog open={open} onClose={!loading ? onClose : undefined} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>🔄 Retry Payout</DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ p: 1.5, bgcolor: "#f5f5f5", borderRadius: 2, mb: 2 }}>
          <Typography fontWeight={600}>{payout?.specialist?.fullName ?? "Unknown"}</Typography>
          <Typography fontSize={13} color="text.secondary">
            Amount: {payout ? fmt(payout.amount, payout.currency) : "—"}
          </Typography>
        </Box>
        {payout?.failureCode && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            <strong>Failure Code:</strong> {payout.failureCode}
            {payout.failureMessage && <><br /><strong>Reason:</strong> {payout.failureMessage}</>}
          </Alert>
        )}
        <Alert severity="info">
          A new idempotency key will be generated for this retry. The original failed payout
          record will remain in history for audit purposes.
        </Alert>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined" color="inherit">Cancel</Button>
        <Button
          variant="contained" color="primary" disabled={loading} onClick={handleRetry}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ minWidth: 120 }}
        >
          {loading ? "Retrying…" : "Confirm Retry"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/* ──────────────────────────────────────────────────────────────────────────── */

export default function AllPayoutsPage() {
  const dispatch  = useDispatch()
  const { loadings } = useSelector(s => s.otherSlice)
  const statusRef = useRef("")
  const [data,        setData]        = useState(null)
  const [retryTarget, setRetryTarget] = useState(null)
  const [status,      setStatus]      = useState("")

  const load = (overrides = {}) => {
    dispatch(fetchAllPayouts({
      page:   overrides.currentPage ?? 1,
      limit:  20,
      status: overrides.status ?? status ?? undefined,
    }))
      .unwrap()
      .then(setData)
      .catch(() => {})
  }

  useEffect(() => { load() }, []) // eslint-disable-line

  const isLoading = loadings?.["wallet/fetchAllPayouts"]

  return (
    <Container maxWidth={false} fixed sx={{ py: 5 }}>
      <Box className="overview-container" sx={{ mb: 3 }}>
        <Typography variant="pageTitle">Payout History</Typography>
        <Typography color="text.secondary" fontSize={14} mt={0.5}>
          All Stripe transfers to specialists across all time. Failed payouts can be retried.
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={status}
            onChange={(e) => { setStatus(e.target.value); load({ status: e.target.value }) }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="succeeded">Succeeded</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <Box className="content-listing">
        <TableContainer className="adminTable1">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {["Specialist", "Amount", "Status", "Transfer ID", "Failure", "Admin", "Note", "Date", "Action"]
                  .map((h, i) => <TableCell key={i}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 9 }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}
                    </TableRow>
                  ))
                : data?.payouts?.map((p, i) => (
                    <TableRow key={i} hover
                      sx={{ td: { borderBottomWidth: i === data.payouts.length - 1 ? 0 : "1px" } }}>
                      <TableCell>
                        <Typography fontSize={14} fontWeight={600} textTransform="capitalize">
                          {p.specialist?.fullName ?? "—"}
                        </Typography>
                        <Typography fontSize={12} color="text.secondary">{p.specialist?.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={700}>{fmt(p.amount, p.currency)}</Typography>
                      </TableCell>
                      <TableCell><StatusChip status={p.status} /></TableCell>
                      <TableCell>
                        {p.stripeTransferId
                          ? (
                              <Typography
                                component="a"
                                href={`https://dashboard.stripe.com/transfers/${p.stripeTransferId}`}
                                target="_blank" rel="noreferrer"
                                fontSize={12}
                                sx={{
                                  fontFamily: "monospace", color: "primary.main",
                                  textDecoration: "none", "&:hover": { textDecoration: "underline" }
                                }}
                              >
                                {p.stripeTransferId.substring(0, 18)}…
                              </Typography>
                            )
                          : <Typography fontSize={12} color="text.disabled">—</Typography>
                        }
                      </TableCell>
                      <TableCell>
                        {p.failureCode
                          ? (
                            <Typography fontSize={12} color="error.main">
                              {p.failureCode}: {p.failureMessage?.substring(0, 40)}{p.failureMessage?.length > 40 ? "…" : ""}
                            </Typography>
                          )
                          : <Typography fontSize={12} color="text.disabled">—</Typography>
                        }
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13} textTransform="capitalize">
                          {p.admin?.fullName ?? "Admin"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13} color="text.secondary">{p.note || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13}>{formatDate(p.createdAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        {p.status === "failed" && (
                          <Button
                            size="small" variant="contained" color="warning"
                            onClick={() => setRetryTarget(p)}
                            sx={{ textTransform: "none", fontSize: 12 }}
                          >
                            🔄 Retry
                          </Button>
                        )}
                        {p.status === "processing" && (
                          <Typography fontSize={11} color="text.disabled">
                            In-flight
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
              }
              {!data?.payouts?.length && !isLoading && (
                <TableRow sx={{ td: { borderBottom: "none" } }}>
                  <TableCell colSpan={9}>
                    <Typography variant="noRecords">No payouts found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          pagination={data?.pagination}
          length={data?.payouts?.length}
          loading={isLoading}
          cb={(f) => load({ currentPage: f.currentPage })}
        />
      </Box>

      <RetryDialog
        open={Boolean(retryTarget)}
        payout={retryTarget}
        onClose={() => setRetryTarget(null)}
        onSuccess={() => load()}
      />
    </Container>
  )
}
